import {
  deltaE2000,
  hsvToRgb,
  type Lab,
  parseHex,
  rgbToHsv,
  rgbToLab,
  toHex,
} from "../scene/color";

/**
 * "Farben aus Bild": the dominant colours of an image, computed from the
 * RGBA pixels of a downscaled canvas. Weighted k-means in CIE Lab over a
 * 5-bit colour histogram (fast and deterministic), near-duplicates merged by
 * ΔE2000, sorted by prominence.
 */

export interface PaletteColor {
  color: string;
  /** Share of the counted pixels (0..1). */
  weight: number;
}

export interface PaletteOptions {
  /** Number of colours to return (default 6). */
  count?: number;
  /** Pixels with less alpha (0..255) are ignored (default 128). */
  minAlpha?: number;
  /** Colours closer than this ΔE2000 are merged (default 12). */
  minDeltaE?: number;
  /** Colours with a smaller share are dropped, e.g. blended edge pixels (default 0.02). */
  minWeight?: number;
}

interface Bin {
  lab: Lab;
  r: number;
  g: number;
  b: number;
  count: number;
}

export function extractPalette(
  pixels: ArrayLike<number>,
  options: PaletteOptions = {},
): PaletteColor[] {
  const count = options.count ?? 6;
  const minAlpha = options.minAlpha ?? 128;
  const minDeltaE = options.minDeltaE ?? 12;
  const minWeight = options.minWeight ?? 0.02;

  // 1. Histogram with 32 levels per channel; bins keep their mean colour.
  const sums = new Map<number, { r: number; g: number; b: number; count: number }>();
  let total = 0;
  for (let index = 0; index + 3 < pixels.length; index += 4) {
    const alpha = pixels[index + 3] ?? 0;
    if (alpha < minAlpha) continue;
    const r = pixels[index] ?? 0;
    const g = pixels[index + 1] ?? 0;
    const b = pixels[index + 2] ?? 0;
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const bin = sums.get(key);
    if (bin) {
      bin.r += r;
      bin.g += g;
      bin.b += b;
      bin.count++;
    } else sums.set(key, { r, g, b, count: 1 });
    total++;
  }
  if (total === 0) return [];
  const bins: Bin[] = [...sums.values()].map((sum) => {
    const rgb = { r: sum.r / sum.count, g: sum.g / sum.count, b: sum.b / sum.count };
    return { ...rgb, lab: rgbToLab(rgb), count: sum.count };
  });
  bins.sort((a, b) => b.count - a.count);

  // 2. Seeds: the most frequent bin, then farthest-point (weighted by frequency).
  const k = Math.min(bins.length, Math.max(count * 2, 8));
  const labDistance = (a: Lab, b: Lab) => (a.l - b.l) ** 2 + (a.a - b.a) ** 2 + (a.b - b.b) ** 2;
  const first = bins[0];
  if (!first) return [];
  const centers: Lab[] = [first.lab];
  while (centers.length < k) {
    let best: Bin | null = null;
    let bestScore = -1;
    for (const bin of bins) {
      const nearest = Math.min(...centers.map((center) => labDistance(center, bin.lab)));
      const score = nearest * Math.sqrt(bin.count);
      if (score > bestScore) {
        bestScore = score;
        best = bin;
      }
    }
    if (!best || bestScore <= 0) break;
    centers.push(best.lab);
  }

  // 3. Weighted k-means.
  const assignment = new Array<number>(bins.length).fill(0);
  for (let iteration = 0; iteration < 12; iteration++) {
    bins.forEach((bin, index) => {
      let bestCenter = 0;
      let bestDistance = Number.POSITIVE_INFINITY;
      centers.forEach((center, centerIndex) => {
        const distance = labDistance(center, bin.lab);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestCenter = centerIndex;
        }
      });
      assignment[index] = bestCenter;
    });
    let moved = false;
    centers.forEach((center, centerIndex) => {
      let l = 0;
      let a = 0;
      let b = 0;
      let weight = 0;
      bins.forEach((bin, index) => {
        if (assignment[index] !== centerIndex) return;
        l += bin.lab.l * bin.count;
        a += bin.lab.a * bin.count;
        b += bin.lab.b * bin.count;
        weight += bin.count;
      });
      if (weight === 0) return;
      const next = { l: l / weight, a: a / weight, b: b / weight };
      if (labDistance(next, center) > 0.01) moved = true;
      centers[centerIndex] = next;
    });
    if (!moved) break;
  }

  // 4. Clusters as mean sRGB colours (exact, no Lab → RGB round trip).
  const clusters = centers
    .map((_, centerIndex) => {
      let r = 0;
      let g = 0;
      let b = 0;
      let weight = 0;
      bins.forEach((bin, index) => {
        if (assignment[index] !== centerIndex) return;
        r += bin.r * bin.count;
        g += bin.g * bin.count;
        b += bin.b * bin.count;
        weight += bin.count;
      });
      if (weight === 0) return null;
      const rgb = { r: r / weight, g: g / weight, b: b / weight };
      return { rgb, lab: rgbToLab(rgb), weight };
    })
    .filter((cluster) => cluster !== null)
    .sort((a, b) => b.weight - a.weight);

  // 5. Merge near-duplicates into the more prominent colour.
  const kept: typeof clusters = [];
  for (const cluster of clusters) {
    const twin = kept.find((other) => deltaE2000(other.lab, cluster.lab) < minDeltaE);
    if (twin) twin.weight += cluster.weight;
    else kept.push({ ...cluster });
  }
  const prominent = kept.filter((cluster) => cluster.weight / total >= minWeight);
  return (prominent.length > 0 ? prominent : kept)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, count)
    .map((cluster) => ({
      color: toHex({ ...cluster.rgb, a: 1 }),
      weight: cluster.weight / total,
    }));
}

/** Adds a colour to a list unless a similar one (ΔE2000) is already there. */
export function addDistinctColor(
  colors: readonly string[],
  color: string,
  minDeltaE = 3,
): string[] {
  const lab = rgbToLab(parseHex(color));
  return colors.some((other) => deltaE2000(rgbToLab(parseHex(other)), lab) < minDeltaE)
    ? [...colors]
    : [...colors, color];
}

// ---------------------------------------------------------------------------
// Colour harmonies (colour picker suggestions)
// ---------------------------------------------------------------------------

export interface ColorHarmonies {
  complementary: string[];
  analogous: string[];
  triadic: string[];
}

function rotateHue(hex: string, degrees: number): string {
  const hsv = rgbToHsv(parseHex(hex));
  const h = (((hsv.h + degrees) % 360) + 360) % 360;
  return toHex({ ...hsvToRgb({ ...hsv, h }), a: 1 });
}

/** Complementary, analogous (±30°) and triadic (±120°) colours of a colour. */
export function colorHarmonies(hex: string): ColorHarmonies {
  return {
    complementary: [rotateHue(hex, 180)],
    analogous: [rotateHue(hex, -30), rotateHue(hex, 30)],
    triadic: [rotateHue(hex, 120), rotateHue(hex, 240)],
  };
}
